import TaxStatementRoutes from 'Agent/1099s/Routes';
import AccountRoutes from 'Agent/Accounts/Routes';
import AgentDocumentationRoutes from 'Agent/AgentDocumentation/Routes';
import AgentFormRoutes from 'Agent/AgentForms/Routes';
import AgentInformationRoutes from 'Agent/AgentInformation/Routes';
import ClaimsRoutes from 'Agent/Claims/Routes';
import CommissionsRoutes from 'Agent/Commissions/Routes';
import PoliciesRoutes from 'Agent/Policies/Routes';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React from 'react';
import {useSelector} from 'react-redux';
import {Redirect, Route} from 'react-router-dom';
import Loading from 'Shared/components/Loading';
import {AgentClaims} from 'Users/models/AgentClaims';
import {UserClaims, UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import agencyFilterSelector from 'Users/selectors/selectAgencyFilters';

export type AgentRoutesProps = {
  claims: UserClaims;
};

const hasAgencyAssociations = flow(
  (agentClaims: AgentClaims) => agentClaims.agencyAssociations,
  associations => associations.length > 0
);

const doesAgentUserHaveAgencies = (agentClaims: AgentClaims) =>
  hasAgencyAssociations(agentClaims) ||
  (agentClaims.impersonatedAgentClaim && hasAgencyAssociations(agentClaims.impersonatedAgentClaim));

const AgentRoutes = ({claims}: AgentRoutesProps) => {
  const selectedAgencyOption = useSelector(agencyFilterSelector);

  const isAgentUser = claims.userTypes
    .map(
      UserTypeClaims.match({
        [UserTypeClaimKey.AgentSite]: doesAgentUserHaveAgencies,
        default: () => false,
      })
    )
    .some(Boolean);

  return (
    <Route
      path="/agent"
      render={props => {
        return isAgentUser ? (
          pipe(
            selectedAgencyOption,
            O.fold(
              () => <Loading />,
              selectedAgency => (
                <>
                  <AccountRoutes {...props} selectedAgency={selectedAgency} />
                  <TaxStatementRoutes {...props} selectedAgency={selectedAgency} />
                  <PoliciesRoutes {...props} selectedAgency={selectedAgency} />
                  <ClaimsRoutes {...props} selectedAgency={selectedAgency} />
                  <CommissionsRoutes {...props} selectedAgency={selectedAgency} />
                  <AgentInformationRoutes {...props} selectedAgency={selectedAgency} />
                  <AgentDocumentationRoutes selectedAgency={selectedAgency} />
                  <AgentFormRoutes {...props} selectedAgency={selectedAgency} claims={claims} />
                </>
              )
            )
          )
        ) : (
          <Redirect to="/unauthorized" />
        );
      }}
    />
  );
};

export default AgentRoutes;
